import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {number} */
  #selectionGeneration = 0;

  /** @type {number} */
  #previewGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const selectionGeneration = ++this.#selectionGeneration;
    if (event.detail?.variantGalleryTarget === this) return;

    const localVariant = event.detail?.localVariant;
    if (!this.#selectMedia(localVariant?.featuredMediaId)) {
      this.#previewMedia(localVariant);
    }

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || selectionGeneration !== this.#selectionGeneration) return;
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (this.#hasSameMedia(newMediaGallery)) {
          this.#selectMedia(detail.resource?.featured_media?.id);
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Selects media already present in the gallery without rebuilding its slides and controls.
   *
   * @param {string | number | null | undefined} mediaId
   * @returns {boolean}
   */
  #selectMedia(mediaId) {
    if (!mediaId || !this.slideshow?.refs.slides) return false;

    const mediaIdString = String(mediaId);
    const slideIndex = this.slideshow.refs.slides.findIndex(
      (slide) => slide.querySelector('[data-media-id]')?.getAttribute('data-media-id') === mediaIdString
    );

    if (slideIndex >= 0) {
      this.#previewGeneration += 1;
      this.slideshow.select(slideIndex, undefined, { animate: false });
      return true;
    }

    return false;
  }

  /**
   * Decodes a missing featured image and swaps only the visible image while the
   * authoritative section response rebuilds the gallery and its controls.
   *
   * @param {import('@shopify/events').ProductSelectPayloadDetail['localVariant']} variant
   */
  #previewMedia(variant) {
    if (!variant?.featuredMediaPreviewUrl || !this.slideshow?.refs.slides) return;

    const currentSlide = this.slideshow.refs.slides.find((slide) => slide.getAttribute('aria-hidden') === 'false');
    const currentImage = /** @type {HTMLImageElement | null} */ (
      currentSlide?.querySelector('.product-media__image')
    );
    if (!currentImage) return;

    const previewGeneration = ++this.#previewGeneration;
    const previewImage = new Image();
    previewImage.decoding = 'async';
    previewImage.fetchPriority = 'high';
    previewImage.src = variant.featuredMediaPreviewUrl;

    const applyPreview = () => {
      if (!this.isConnected || previewGeneration !== this.#previewGeneration) return;

      currentImage.removeAttribute('srcset');
      currentImage.removeAttribute('sizes');
      currentImage.src = variant.featuredMediaPreviewUrl;
      currentImage.alt = variant.featuredMediaAlt;
      currentImage.fetchPriority = 'high';
    };

    previewImage.decode().then(applyPreview, applyPreview);
  }

  /**
   * @param {Element} newMediaGallery
   */
  #hasSameMedia(newMediaGallery) {
    const getMediaIds = (gallery) =>
      Array.from(gallery.querySelectorAll('slideshow-component slideshow-slide .product-media[data-media-id]'))
        .map((media) => media.getAttribute('data-media-id'))
        .filter(Boolean)
        .sort();

    const currentIds = getMediaIds(this);
    const newIds = getMediaIds(newMediaGallery);

    return currentIds.length === newIds.length && currentIds.every((id, index) => id === newIds[index]);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
